import LoadingSpinnerFull from "../../../components/common/loading-spinner/loading-spinner-full";
import { useState } from "react";
import { HiOutlineXMark } from "react-icons/hi2";
import { FaCrown } from "react-icons/fa";
import Header from "../../../components/common/header";
import { gql } from "../../../__generated__";
import { Review } from "../../../__generated__/graphql";
import { useQuery, useMutation } from "@apollo/client";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import heroGray from "../../../assets/images/hero_gray.png";
import { notify, notifyError } from "../../../utils/toast";
import bestReviewImg from "./best-review-ex.png";

export const GET_REVIEW_BY_SELLER = gql(`
  query getReviewBySeller {
    getReviewBySeller {
      id
      user {
        maskedName
      }
      orderType
      reviewImages {
        img
      }
      content
      createdAt
      isBestReview
    }
  }
`);

const CHOOSE_BEST_REVIEW = gql(`
  mutation ChooseBestReview($reviewId: Int!) {
    chooseBestReview(reviewId: $reviewId) {
      ok
      error
    }
  }
`);

const BossReview = () => {
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [isBestReviewModalOpen, setIsBestReviewModalOpen] = useState(false);
  const [selectedReviewForBest, setSelectedReviewForBest] =
    useState<Review | null>(null);
  const [isBestReviewInfoOpen, setIsBestReviewInfoOpen] = useState(false);

  const { data, loading, refetch } = useQuery(GET_REVIEW_BY_SELLER, {
    fetchPolicy: "network-only",
  });

  const [chooseBestReview] = useMutation(CHOOSE_BEST_REVIEW);

  const handleChooseBestReview = (review: Review) => {
    setSelectedReviewForBest(review);
    setIsBestReviewModalOpen(true);
  };

  const confirmChooseBestReview = async () => {
    if (!selectedReviewForBest) return;

    try {
      const result = await chooseBestReview({
        variables: {
          reviewId: selectedReviewForBest.id,
        },
      });

      if (result.data?.chooseBestReview.ok) {
        notify("베스트리뷰로 선정되었습니다 👑");
        refetch(); // 리스트 새로고침
      } else {
        notifyError(
          result.data?.chooseBestReview.error ||
            "베스트리뷰 선정에 실패했습니다"
        );
      }
    } catch (error) {
      notifyError("베스트리뷰 선정 중 오류가 발생했습니다");
    }

    setIsBestReviewModalOpen(false);
    setSelectedReviewForBest(null);
  };

  if (loading) {
    return <LoadingSpinnerFull />;
  }

  return (
    <div className="w-full min-h-screen h-full bg-white overflow-y-scroll">
      <Header title="우리가게 리뷰" showBackButton />

      <div className="w-full p-4">
        <div className="mb-6 bg-gray-50 rounded-2xl p-6 shadow-sm">
          <div className="space-y-2 text-gray-700">
            <p className="text-lg font-semibold">
              사장님의 매장에서 만난 소중한 손님들이 남긴 리뷰를 모아두었습니다.
            </p>
            <p className="text-md">
              하나하나 클릭해서 손님들의 따뜻한 이야기를 직접 확인해보세요.
            </p>

            {/* 베스트리뷰 정보 툴팁 */}
            <div className="mt-4">
              <button
                onClick={() => setIsBestReviewInfoOpen(!isBestReviewInfoOpen)}
                className="flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200"
              >
                <span className="mr-1">베스트리뷰는 무엇인가요?</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isBestReviewInfoOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* 베스트리뷰 설명 영역 */}
              {isBestReviewInfoOpen && (
                <div className="mt-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm text-gray-700 space-y-3">
                    <p className="font-medium">
                      베스트리뷰를 선정하면, 고객에게 알림이 가고, 매장 노출에
                      함께 노출됩니다.
                    </p>

                    {/* 예시 사진 영역 */}
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-2">예시 사진:</p>
                      <div className="bg-gray-100 rounded-lg p-2 text-center text-gray-400 text-sm">
                        {/* 사용자가 추가할 예시 사진이 들어갈 자리 */}
                        <div className="w-full h-36 bg-gray-200 rounded flex items-center justify-center">
                          <img src={bestReviewImg} alt="베스트리뷰 예시" />
                        </div>
                        <p className="mt-2 text-xs">예시 사진</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {data?.getReviewBySeller.map((review: any) => (
            <div key={review.id} className="flex flex-col">
              <div
                onClick={() => setSelectedReview(review)}
                className="aspect-square rounded-lg overflow-hidden relative cursor-pointer shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                {review.reviewImages && review.reviewImages.length > 0 ? (
                  <ResponsiveCDNImage
                    src={review.reviewImages[0].img}
                    alt="리뷰 이미지"
                    className="w-full h-full object-cover"
                    sizeRatio={1 / 2}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                    <img
                      src={heroGray}
                      alt="리뷰 이미지"
                      className="w-full h-[50px] object-contain rounded-lg"
                    />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="flex flex-col justify-center gap-1 mb-2">
                    <div className="flex">
                      <span className="font-semibold text-white bg-black/50 px-2 py-1 rounded-full text-sm truncate line-clamp-1">
                        {review.user?.maskedName}
                      </span>
                    </div>
                    <div className="flex">
                      <span className="font-medium text-sm text-white bg-black/50 px-2 py-1 rounded-full line-clamp-1">
                        {review.orderType === "PACK" ? "예약팩" : "실시간팩"}
                      </span>
                    </div>
                  </div>
                  <div className="text-white line-clamp-1 text-sm mb-1">
                    {review.content}
                  </div>
                  <div className="text-white/70 text-xs">
                    {new Date(review.createdAt).toLocaleDateString("ko-KR", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                </div>
              </div>

              {/* 베스트리뷰 상태 표시 및 선정 버튼 (카드 바깥) */}
              <div className="mt-3 flex justify-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChooseBestReview(review);
                  }}
                  disabled={review.isBestReview}
                  className={`flex items-center text-xs px-4 py-2 rounded-full font-semibold transition-colors duration-200 ${
                    review.isBestReview
                      ? "bg-yellow-400 text-yellow-900 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-black/80 text-white"
                  }`}
                >
                  {review.isBestReview && <FaCrown className="w-3 h-3 mr-1" />}
                  {review.isBestReview ? "베스트리뷰" : "베스트리뷰 선정"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 베스트리뷰 선정 확인 모달 */}
      {isBestReviewModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded p-6">
            <div className="break-keep font-bold text-3xl p-8 text-center">
              베스트리뷰로 선정하시겠습니까?
            </div>
            <div className="break-keep font-semibold text-xl pb-4 text-gray-400 text-center">
              베스트리뷰는 매장당 <br />
              하나만 선정할 수 있습니다.
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setIsBestReviewModalOpen(false)}
                className="bg-gray-300 px-4 py-2 rounded"
              >
                취소
              </button>
              <button
                onClick={confirmChooseBestReview}
                className="ml-2 bg-blue-500 text-white px-8 py-2 rounded"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[90%] max-w-[500px] max-h-[90vh] relative">
            <div className="p-6 overflow-y-auto max-h-[90vh]">
              <div className="flex justify-between items-start gap-2 mb-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-600">
                      {selectedReview.user?.maskedName}
                    </span>
                    <span className="font-medium text-md text-gray-600">
                      {selectedReview.orderType === "PACK"
                        ? "예약구매"
                        : "실시간구매"}
                    </span>
                  </div>
                  <div className="text-gray-500">
                    {new Date(selectedReview.createdAt).toLocaleDateString(
                      "ko-KR",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )}{" "}
                    {new Date(selectedReview.createdAt).toLocaleTimeString(
                      "ko-KR",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      }
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedReview(null)}
                  className="text-gray-500 active:text-gray-700"
                >
                  <HiOutlineXMark className="w-6 h-6" />
                </button>
              </div>

              {selectedReview.reviewImages &&
                selectedReview.reviewImages.length > 0 && (
                  <div className="mb-4">
                    <ResponsiveCDNImage
                      src={selectedReview.reviewImages[0]?.img}
                      alt="리뷰 이미지"
                      className="w-full h-[300px] object-cover rounded-lg"
                    />
                  </div>
                )}
              <div className="text-gray-700 whitespace-pre-wrap mb-4">
                {selectedReview.content}
              </div>

              {/* 모달 내부 베스트리뷰 상태 및 선정 버튼 */}
              <div className="flex flex-col items-center">
                {selectedReview.isBestReview && (
                  <div className="flex items-center bg-yellow-400 text-yellow-900 px-4 py-2 rounded-lg text-lg font-bold mb-4">
                    <FaCrown className="w-5 h-5 mr-2" />
                    베스트리뷰로 선정된 리뷰입니다 👑
                  </div>
                )}
                <button
                  onClick={() => {
                    setSelectedReview(null);
                    handleChooseBestReview(selectedReview);
                  }}
                  disabled={selectedReview.isBestReview}
                  className={`px-6 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                    selectedReview.isBestReview
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-black/80 text-white"
                  }`}
                >
                  {selectedReview.isBestReview
                    ? "이미 베스트리뷰입니다"
                    : "베스트리뷰 선정"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BossReview;
